import { Repeat2, SmilePlus, Users } from 'lucide-react';

import { useTranslation } from '@/utils/i18n';
import type { ImpactTotalsData } from '@/types';

/**
 * B3+B4 — global impact counters at the top of "My Reco": total rebounds,
 * total reactions, and total people who discovered a sound thanks to the user.
 * Renders nothing until totals arrive (older cached payloads have none).
 */
export default function ImpactTotals({ totals }: { totals: ImpactTotalsData | undefined }) {
  const { t } = useTranslation();
  if (!totals) return null;

  // Order is deliberate (G, sprint 2.1.0): discoveries first — the primary
  // signal — then reactions, then rebounds.
  const items = [
    {
      testid: 'impact-total-discovered',
      icon: <Users size={18} className="tw:text-blue-primary" />,
      value: totals.discovered,
      label: t('myReco.totals.discovered'),
    },
    {
      testid: 'impact-total-reactions',
      icon: <SmilePlus size={18} className="tw:text-yellow-primary" />,
      value: totals.reactions,
      label: t('myReco.totals.reactions'),
    },
    {
      testid: 'impact-total-rebounds',
      icon: <Repeat2 size={18} className="tw:text-green-primary" />,
      value: totals.rebounds,
      label: t('myReco.totals.rebounds'),
    },
  ];

  return (
    <div className="tw:mb-4 tw:grid tw:grid-cols-3 tw:gap-2" data-testid="impact-totals">
      {items.map(({ testid, icon, value, label }) => (
        <div
          key={testid}
          data-testid={testid}
          className="tw:flex tw:flex-col tw:items-center tw:gap-1 tw:rounded-2xl tw:bg-dark-1/60 tw:px-2 tw:py-3 tw:text-center"
        >
          {icon}
          <span className="tw:text-xl tw:font-bold tw:text-white">{value}</span>
          <span className="tw:legals tw:text-grey-3">{label}</span>
        </div>
      ))}
    </div>
  );
}
